import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2 } from "lucide-react";
import type { AutopilotConnectorDiagnostic } from "@/lib/api/autopilot";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useTenantHref } from "@/lib/urls";

type ConnectorType = AutopilotConnectorDiagnostic["connector"];

type AutopilotOnboardingWizardProps = {
  diagnostics: AutopilotConnectorDiagnostic[];
  onRunCanary: () => Promise<void>;
  onToggleConnector: (connector: ConnectorType, enabled: boolean) => void;
  onConfigureConnector: (connector: ConnectorType) => void;
  toggleState: {
    isPending: boolean;
    pendingConnector?: ConnectorType;
  };
  isDiagnosticsLoading: boolean;
  isRunningCanary: boolean;
};

const statusBadgeStyles: Record<"complete" | "pending", string> = {
  complete: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-200",
  pending: "bg-slate-200/60 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300",
};

const CONNECTOR_ONBOARDING_STEPS: Array<{
  connector: ConnectorType;
  key: "google" | "meta" | "waze" | "nextdoor" | "sms" | "email";
  defaultTitle: string;
  defaultDescription: string;
}> = [
  {
    connector: "google_ads",
    key: "google",
    defaultTitle: "Connect Google Ads",
    defaultDescription: "Authorize sandbox access so Autopilot can sync conversions.",
  },
  {
    connector: "meta_ads",
    key: "meta",
    defaultTitle: "Connect Meta Conversions",
    defaultDescription: "Use sandbox credentials to enable offline conversions and match testing.",
  },
  {
    connector: "waze_ads",
    key: "waze",
    defaultTitle: "Connect Waze Ads",
    defaultDescription: "Link sandbox routing so Autopilot can manage pins and spend safely.",
  },
  {
    connector: "nextdoor_ads",
    key: "nextdoor",
    defaultTitle: "Connect Nextdoor Ads",
    defaultDescription: "Authorize neighborhood campaigns to unlock local reach diagnostics.",
  },
  {
    connector: "sms_campaign",
    key: "sms",
    defaultTitle: "Connect SMS campaigns",
    defaultDescription: "Sync sandbox credentials so Autopilot can orchestrate text automations.",
  },
  {
    connector: "email_campaign",
    key: "email",
    defaultTitle: "Connect email campaigns",
    defaultDescription: "Enable sandbox sending to test nurture flows before production launch.",
  },
];

const AutopilotOnboardingWizard = ({
  diagnostics,
  onRunCanary,
  onToggleConnector,
  onConfigureConnector,
  toggleState,
  isDiagnosticsLoading,
  isRunningCanary,
}: AutopilotOnboardingWizardProps) => {
  const { t } = useTranslation("autopilot");
  const consentPolicyHref = useTenantHref("settings");
  const sandboxDataAvailable = diagnostics.some((diag) => diag.hasSandboxData);
  const canaryComplete = diagnostics.some((diag) => diag.recentUploads.length > 0);

  const buildToggleHelp = (diag?: AutopilotConnectorDiagnostic) =>
    diag?.credentialStatus !== "active"
      ? t("prototype.onboarding.toggle.disabled", { defaultValue: "Enable after credentials are active." })
      : t("prototype.onboarding.toggle.help", { defaultValue: "Pause uploads while you triage issues." });

  const connectorSteps = CONNECTOR_ONBOARDING_STEPS.map((step) => {
    const diag = diagnostics.find((entry) => entry.connector === step.connector);
    const pending = toggleState.isPending && toggleState.pendingConnector === step.connector;
    const disabled = isDiagnosticsLoading || toggleState.isPending || diag?.credentialStatus !== "active";

    return {
      id: step.key,
      title: t(`prototype.onboarding.steps.${step.key}.title`, { defaultValue: step.defaultTitle }),
      description: t(`prototype.onboarding.steps.${step.key}.description`, {
        defaultValue: step.defaultDescription,
      }),
      complete: diag?.credentialStatus === "active",
      action: (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onConfigureConnector(step.connector)}
        >
          {t(`prototype.onboarding.steps.${step.key}.action`, { defaultValue: "Configure credentials" })}
        </Button>
      ),
      toggle: {
        id: `autopilot-onboarding-${step.key}-toggle`,
        connector: step.connector,
        checked: diag?.enabled ?? false,
        pending,
        disabled,
        help: buildToggleHelp(diag),
      },
    };
  });

  const steps = [
    ...connectorSteps,
    {
      id: "consent",
      title: t("prototype.onboarding.steps.consent.title", { defaultValue: "Upload consent policy" }),
      description: t("prototype.onboarding.steps.consent.description", {
        defaultValue: "Mirror your EN/ES prompts and attach the sandbox consent ledger for review.",
      }),
      complete: sandboxDataAvailable,
      action: (
        <Button asChild variant="outline" size="sm">
          <Link to={consentPolicyHref}>
            {t("prototype.onboarding.steps.consent.action", { defaultValue: "Manage policies" })}
          </Link>
        </Button>
      ),
    },
    {
      id: "sandbox",
      title: t("prototype.onboarding.steps.sandbox.title", { defaultValue: "Validate sandbox data" }),
      description: t("prototype.onboarding.steps.sandbox.description", {
        defaultValue: "Run a canary upload once sandbox data is available.",
      }),
      complete: canaryComplete,
      action: (
        <Button
          size="sm"
          className="bg-emerald-500 text-white hover:bg-emerald-600"
          onClick={() => onRunCanary()}
          disabled={!sandboxDataAvailable || isRunningCanary || isDiagnosticsLoading}
        >
          {isRunningCanary ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {t("prototype.onboarding.steps.sandbox.run", { defaultValue: "Run canary upload" })}
        </Button>
      ),
    },
  ];

  return (
    <Card className="border-slate-200/70 bg-white/70 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/60">
      <CardHeader>
        <CardTitle>{t("prototype.onboarding.title", { defaultValue: "Launch sandbox connectors" })}</CardTitle>
        <CardDescription>
          {t("prototype.onboarding.subtitle", {
            defaultValue: "Work through the checklist to move connectors into sandbox mode safely.",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {steps.map((step, index) => {
          const status: "complete" | "pending" = step.complete ? "complete" : "pending";
          return (
            <div
              key={step.id}
              className="rounded-xl border border-slate-200/60 bg-white/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/40"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-3">
                  <Badge variant="secondary" className="mt-0.5 text-xs tracking-wide">
                    {String(index + 1).padStart(2, "0")}
                  </Badge>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">{step.title}</h3>
                    <p className="text-xs text-muted-foreground">{step.description}</p>
                  </div>
                </div>
                <Badge className={cn("self-start text-xs font-medium", statusBadgeStyles[status])}>
                  {status === "complete"
                    ? t("prototype.onboarding.status.complete", { defaultValue: "Complete" })
                    : t("prototype.onboarding.status.pending", { defaultValue: "Pending" })}
                </Badge>
              </div>
              <div className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground">
                <div className="flex flex-wrap items-center gap-3">
                  {step.action}
                  {step.toggle ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        id={step.toggle.id}
                        checked={step.toggle.checked}
                        disabled={step.toggle.disabled}
                        onCheckedChange={(checked) => onToggleConnector(step.toggle.connector, checked)}
                        aria-label={t("prototype.onboarding.toggle.label", { defaultValue: "Sandbox connector" })}
                      />
                      <label htmlFor={step.toggle.id} className="text-xs text-muted-foreground">
                        {t("prototype.onboarding.toggle.label", { defaultValue: "Sandbox connector" })}
                      </label>
                      {step.toggle.pending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {step.toggle ? (
                  <p className="text-xs text-muted-foreground">{step.toggle.help}</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AutopilotOnboardingWizard;
